import React from "react";
import { type NavigateFunction } from "react-router-dom";
import { useLocalization } from "../../context/LocalizationContext";
import { type Question } from "../../dtos/questionnaire";
import CompletedBadge from "../../components/shared/CompletedBadge";
import { convertToArabicNumerals } from "../../utils/helpers";
import "./styles.css";

export interface AssessmentCategory {
  id: number;
  name: string;
  title: string;
  questionCount: number;
  questions: Question[];
}

export interface CategoryTabProps {
  category: AssessmentCategory;
  categories: AssessmentCategory[];
  currentCategory: number | null;
  completedCategories: number[];
  savedAnswers: Record<number, Record<number, number | number[]>>;
  setCurrentCategory: (id: number) => void;
  navigate: NavigateFunction;
}

const CategoryTab: React.FC<CategoryTabProps> = ({
  category,
  categories,
  currentCategory,
  completedCategories,
  savedAnswers,
  setCurrentCategory,
  navigate,
}) => {
  const { t, isRTL } = useLocalization();

  const handleCategoryClick = () => {
    const assessmentData = categories.find((c) => c.id === category.id);
    if (assessmentData) {
      setCurrentCategory(category.id);
      navigate(`/assessment/${category.id}`, { state: { assessment: assessmentData } });
    }
  };

  const categoryAnswers = savedAnswers[category.id];
  const answeredCount = categoryAnswers ? Object.keys(categoryAnswers).length : 0;
  const totalQuestions = category.questionCount || 0;
  const isActive = currentCategory == category.id;
  const isCompleted = completedCategories.includes(category.id);

  return (
    <div
      className={`category-tab ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}
      onClick={handleCategoryClick}
      role="button"
      tabIndex={0}
      aria-current={isActive ? "true" : undefined}
      onKeyDown={(e) => e.key === "Enter" && handleCategoryClick()}
    >
      <span className="category-title">{category.title}</span>
      <div className="category-footer">
        {isCompleted ? (
          <CompletedBadge className="category-check" />
        ) : (
          <span className="category-progress">
            {isRTL
              ? `${convertToArabicNumerals(answeredCount)}/${convertToArabicNumerals(totalQuestions)}`
              : `${answeredCount}/${totalQuestions}`}
          </span>
        )}
      </div>
    </div>
  );
};

export default CategoryTab;
